import React, { useEffect } from "react";
import { BusFront, LogOut, Map, Moon, ShieldCheck, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import BusLoading from "../components/BusLoading";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { GuideWorkDashboard } from "./DashboardPage";
import DriverDashboardPage from "./Drivers/DriverDashboardPage";
import GuideApplicationPage from "./GuideApplicationPage";
import ProviderPortalSignIn from "../components/ProviderPortalSignIn";

export default function ProviderPortalPage({ portalType }) {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { user, loading, logout, refreshProfile } = useAuth();
  const isDriverPortal = portalType === "driver";
  const targetRole = isDriverPortal ? "driver" : "guide";

  useEffect(() => {
    document.title = isDriverPortal ? t("work_portal.driver_title_document") : t("work_portal.guide_title_document");
  }, [isDriverPortal, t]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const timer = window.setInterval(() => refreshProfile(), 12000);
    return () => window.clearInterval(timer);
  }, [refreshProfile, user?.id]);

  if (loading) return <BusLoading className="bg-[#F7F6F3] dark:bg-[#080a10]" />;

  const Icon = isDriverPortal ? BusFront : Map;
  const title = isDriverPortal ? t("work_portal.driver_title") : t("work_portal.guide_title");
  const isOtherProvider = user && user.role !== targetRole;

  return (
    <div className="min-h-screen bg-[#F7F6F3] text-slate-900 dark:bg-[#080a10] dark:text-[#f5f7fa]">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-[#F7F6F3]/95 px-4 py-4 backdrop-blur dark:border-[#272c35] dark:bg-[#080a10]/95 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-400 text-slate-950"><Icon className="h-5 w-5" aria-hidden="true" /></span>
          <div className="min-w-0 flex-1"><p className="text-xs font-black uppercase tracking-[.18em] text-amber-600 dark:text-amber-400">UzTravell · {title}</p>{user && <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">{user.name}</p>}</div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={toggleTheme} aria-label={t("nav.toggle_theme")} className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 text-amber-500 transition hover:border-amber-400 hover:bg-amber-50 dark:border-[#353b46] dark:hover:bg-slate-800">
              <Sun className={`absolute h-4 w-4 transition-all duration-300 ${theme === "dark" ? "scale-50 -rotate-90 opacity-0" : "scale-100 rotate-0 opacity-100"}`} />
              <Moon className={`absolute h-4 w-4 text-amber-400 transition-all duration-300 ${theme === "dark" ? "scale-100 rotate-0 opacity-100" : "scale-50 rotate-90 opacity-0"}`} />
            </button>
            <LanguageSwitcher />
            {user && <button type="button" onClick={logout} aria-label={t("work_portal.logout")} className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-amber-400 hover:text-amber-700 dark:border-[#353b46] dark:text-slate-300 dark:hover:text-amber-300"><LogOut className="h-4 w-4" /></button>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        {!user ? (
          <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-6 text-center dark:border-[#272c35] dark:bg-[#11141b] sm:p-9">
            <p className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400"><ShieldCheck className="h-6 w-6" aria-hidden="true" /></p>
            <h1 className="mt-5 text-2xl font-black">{t("work_portal.use_main_site_title")}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">{t("work_portal.use_main_site_text")}</p>
            <ProviderPortalSignIn />
          </div>
        ) : isOtherProvider ? (
          <section className="mx-auto max-w-2xl rounded-3xl border border-amber-200 bg-white p-6 dark:border-amber-400/20 dark:bg-[#11141b] sm:p-9">
            <h1 className="text-2xl font-black">{t("work_portal.other_provider_title")}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">{t("work_portal.other_provider_text")}</p>
          </section>
        ) : (
          <>
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[.18em] text-amber-600 dark:text-amber-400"><ShieldCheck className="h-4 w-4" />{t("work_portal.private_portal")}</p>
            <h1 className="mt-2 text-3xl font-black sm:text-4xl">{isDriverPortal ? t("work_portal.driver_heading") : t("work_portal.guide_heading")}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">{isDriverPortal ? t("work_portal.driver_portal_subtitle") : t("work_portal.guide_portal_subtitle")}</p>
            {isDriverPortal ? <DriverDashboardPage /> : user.role === "guide" && user.is_approved ? <GuideWorkDashboard /> : <GuideApplicationPage />}
          </>
        )}
      </main>
    </div>
  );
}
